import React from "react";
import SEO from "../components/SEO";
import SoftwareSolutions from "../components/CDSSolutions";
import ContactCTA from "../components/ContactCTA";
import SoftwareDevice from "../assets/images/cdsSection.png";
import MonitorIcon from "../assets/svg/monitor.svg";
import MeterIcon from "../assets/svg/meter.svg";
import VernIcon from "../assets/svg/vern.svg";
import ThumbsIcon from "../assets/svg/thumbsUp.svg";
import FAQ from "../components/FAQ";
import SpanaIcon from "../assets/svg/spana.svg";
import UiIcon from "../assets/svg/ui.svg";
import CloudIcon from "../assets/svg/cloud.svg";
import MultiIcon from "../assets/svg/multi-dev.svg";
function SoftwarePage() {
  const faqs = [
    {
      question: "What is VernPOS Customer Display System (CDS)?",
      answer:
        "VernPOS CDS is a customer-facing display that shows real-time updates such as queue numbers, order progress, and payment details. It enhances transparency, reduces confusion, and improves the overall checkout experience. ",
    },
    {
      question: "How does CDS integrate with VernPOS?",
      answer:
        "CDS connects directly to the VernPOS system, synchronising order and payment data in real time. This ensures customers see accurate updates instantly, while staff enjoy a smooth, automated workflow without extra steps.",
    },
    {
      question: "Can I customise the display layout or branding?",
      answer:
        "Yes. VernPOS CDS allows customisation of logos, colours, and layout designs to match your brand's identity — giving your customer display a consistent and professional look.",
    },
    {
      question: "What makes VernPOS CDS different from others?",
      answer:
        "VernPOS CDS stands out with its clean, intuitive interface and colour-coded order status display. These features make it easy for both staff and customers to understand order progress at a glance, improving communication and efficiency.",
    },
    {
      question: "Is it difficult for staff to learn and use?",
      answer:
        "Not at all. CDS is designed for simplicity — it runs seamlessly with VernPOS, requiring minimal setup and almost no learning curve for staff.",
    },
    {
      question: "What devices and setups does CDS support?",
      answer:
        "VernPOS CDS supports a wide range of POS terminals and secondary displays, including customer-facing tablets and monitors. It can be easily configured for single or multi-counter environments.",
    },
  ];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="POS Software Solutions | VernPOS Malaysia"
        description="Discover our comprehensive POS software solutions. Cloud-based, scalable, and designed for retail, F&B, and service businesses across Malaysia."
        keywords="POS software Malaysia, cloud POS system, retail software, F&B software, restaurant POS, inventory management, VernPOS"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Two-tone Background */}
        <div className="absolute inset-0">
          {/* Red Background - Top 70% */}
          <div className="absolute top-0 left-0 right-0 h-[70%] bg-gradient-to-br bg-vern to-red-700"></div>
          {/* White Background - Bottom 30% */}
          <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-white"></div>
          {/* Smooth Curved Transition */}
          <div className="absolute left-0 right-0" style={{ top: "100%", height: "10%" }}>
            <svg viewBox="0 0 1200 120" className="w-full h-full" preserveAspectRatio="none">
              <path d="M0,0 C400,80 800,80 1200,0 L1200,120 L0,120 Z" fill="white" />
            </svg>
          </div>
        </div>
        {/* Content Container */}
        <div className="relative z-10 container mx-auto max-w-6xl px-4 pt-32 pb-16">
          {/* Centered Text Content */}
          <div className="text-center mb-36">
            <h1 className="text-5xl md:text-5xl lg:text-5xl font-bold text-white mb-6 leading-tight max-w-4xl mx-auto">
              Turn Queues into
              <br />
              Communication with Customer
              <br />
              Display Management System
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-6xl mx-auto">
              Keep every customer informed with real-time updates on queue status, order details, and payment confirmation. Integrated with VernPOS,
              CDM enhances transparency, builds trust, and delivers smoother checkout experience.
            </p>
          </div>
          {/* POS Software Image - Centered and Overlapping */}
          <div className="relative flex justify-center">
            <div className="relative z-20 transform hover:scale-105 transition-transform duration-300">
              <img
                src={SoftwareDevice}
                alt="Professional POS Software Interface"
                className="w-full max-w-3xl h-auto object-contain drop-shadow-2xl"
                style={{
                  filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.3)) drop-shadow(0 0 20px rgba(220,38,38,0.2))",
                  marginTop: "-100px", // Overlap with navbar
                }}
              />
            </div>
            {/* Subtle Glow Effect */}
            <div
              className="absolute inset-0 z-10 blur-3xl opacity-30"
              style={{
                background: "radial-gradient(ellipse at center, rgba(220,38,38,0.4) 0%, transparent 70%)",
                transform: "scale(1.2)",
              }}
            ></div>
          </div>
        </div>
        {/* Additional Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/3 right-16 w-32 h-32 bg-red-400/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
      </section>
      {/* Built for Efficiency Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-6xl font-bold text-black mb-4">
              Designed for Clarity. Built for
              <br />
              Better Communication
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              No guessing. No waiting in the dark. No confusion. Just customers who know what’s happening.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={MonitorIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-1 my-2">Real-Time Order Updates</h3>
                <p className="text-gray-600 font-medium">
                  Display live order progress, queue numbers, and order status—keeping customers informed every step of the way.
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={MeterIcon} alt="Performance Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Color-Coded Status Display</h3>
                <p className="text-gray-600 font-medium">
                  A clear visual system that uses distinct colors for each order stage, allowing customers and staff to recognize order readiness at a
                  glance.
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={VernIcon} alt="VernPOS Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Tap to Prioritize or Bump</h3>
                <p className="text-gray-600 font-medium">Easily mark items as urgent, bump completed ones, or recall with a tap.</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={ThumbsIcon} alt="Thumbs Up Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Seamless POS Integration</h3>
                <p className="text-gray-600 font-medium">
                  Seamless data synchronisation across VernPOS ecosystem in real time for a smooth, experience.
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            {/* Google Play Badge */}
            <div className="inline-block">
              <a
                href="https://play.google.com/store/apps/details?id=com.vernpos.posnative"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
                aria-label="Download Vern POS on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                  alt="Get Vern POS on Google Play"
                  className="h-20 w-auto md:h-24"
                />
              </a>
            </div>
            {/* <button className="bg-vern hover:bg-red-700 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-300">
                            Start Free Trial
                        </button> */}
          </div>
        </div>{" "}
      </section>
      {/* Software Solutions */}
      <SoftwareSolutions />
      {/* Designed for Simplicity Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Less Guessing. Happier Customers.</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              With Customer Display Management System, Customer no longer needs to guess when their orders gonna arrive.
            </p>
          </div>
          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Real-Time Tracking */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={SpanaIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Real-Time Tracking</h3>
              <p className="text-gray-600 text-sm">See every order in progress and spot bottlenecks before they happen.</p>
            </div>
            {/* Instant POS Sync */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={UiIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Instant POS Sync</h3>
              <p className="text-gray-600 text-sm">Orders flow straight from kitchen to front office — zero lag, zero hassle.</p>
            </div>
            {/* Empower Every Station */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={CloudIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Empower Every Station</h3>
              <p className="text-gray-600 text-sm">Keep every order synchronized — no missed orders, no delays.</p>
            </div>
            {/* Actionable Insights */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={MultiIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Actionable Insights</h3>
              <p className="text-gray-600 text-sm">Track order efficiency, prep times, and team performance at a glance.</p>
            </div>
          </div>
          {/* Google Play Badge */}
          <div className="text-center">
            <div className="inline-block">
              <a
                href="https://play.google.com/store/apps/details?id=com.vernpos.posnative"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
                aria-label="Download Vern POS on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                  alt="Get Vern POS on Google Play"
                  className="h-20 w-auto md:h-24"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* FQA  */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <FAQ title="Frequently Asked Questions" faqs={faqs} />
      </div>
      {/* Contact CTA */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <ContactCTA />
      </div>
    </div>
  );
}
export default SoftwarePage;
